// The optimistic brake control (Brief 4 / P2-B). Stop / resume one session from
// the same seat the Orchestrator judges it (the evidence surface). The button
// echoes the intent INSTANTLY (<100ms — no round-trip gates it) as a visibly
// PENDING state; the CONFIRMED state arrives only over the presence SSE and snaps
// as fact (critically damped via the motion tokens — never a spring, never a
// premature "all clear"). All the trust-bearing logic lives in the host-tested
// `brakeReducer`; this is the thin React shell + the I/O.

import { useEffect, useReducer, useRef } from "react";
import {
  getPresence,
  pauseSession,
  resumeSession,
  subscribeChat,
} from "../api/client";
import { brakeDisplay, brakeReducer, type BrakeState } from "./brakeReducer";
import "./steer.css";

/** Give up waiting for a server confirm after this and reconcile to the latest
 * truth — never leave the control stuck on optimism. */
const PENDING_TIMEOUT_MS = 8000;

const INITIAL: BrakeState = { status: "", pending: null };

export function BrakeControl({ threadId }: { threadId: number }) {
  const [state, dispatch] = useReducer(brakeReducer, INITIAL);
  const timer = useRef<number | null>(null);

  // Initial presence for this one session + live presence over the SSE — both
  // feed the SAME reducer as the optimistic echo (one source of truth).
  useEffect(() => {
    let alive = true;
    getPresence()
      .then((r) => {
        const s = r.sessions.find((x) => x.thread_id === threadId);
        if (alive && s) dispatch({ type: "presence", status: s.status });
      })
      .catch(() => {});
    const unsub = subscribeChat(
      () => {},
      () => {},
      (p) => {
        if (p.thread_id === threadId) {
          dispatch({ type: "presence", status: p.status });
        }
      },
    );
    return () => {
      alive = false;
      unsub();
    };
  }, [threadId]);

  // Clear the safety timer on unmount.
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  function arm() {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(
      () => dispatch({ type: "timeout" }),
      PENDING_TIMEOUT_MS,
    );
  }

  function stop() {
    dispatch({ type: "send", intent: "pause" }); // instant optimistic echo
    arm();
    pauseSession(threadId).catch(() => dispatch({ type: "fail" }));
  }
  function resume() {
    dispatch({ type: "send", intent: "resume" });
    arm();
    resumeSession(threadId).catch(() => dispatch({ type: "fail" }));
  }

  const display = brakeDisplay(state);
  const isPaused = state.status === "paused";

  return (
    <div
      className="wt-brake"
      data-pending={display.pending ? "true" : "false"}
      data-status={state.status}
    >
      <span className="wt-brake__state" aria-live="polite">
        {display.label || "—"}
      </span>
      {display.pending ? (
        <button type="button" className="wt-brake__btn" disabled>
          {display.label}
        </button>
      ) : isPaused ? (
        <button
          type="button"
          className="wt-brake__btn wt-brake__btn--resume"
          onClick={resume}
        >
          Resume
        </button>
      ) : (
        <button
          type="button"
          className="wt-brake__btn wt-brake__btn--stop"
          onClick={stop}
        >
          Stop
        </button>
      )}
    </div>
  );
}
